import React from 'react'
import { Helmet } from "react-helmet-async";
import { CopyBlock , a11yLight, github} from 'react-code-blocks';

// Components
import HeaderRegComponent from '../../Components/HeaderRegComponent'
import FooterComponent from '../../Components/FooterComponent'

const VerifyingSearchAPIToken = () => {
    const headerExample = `headers: {
  "Authorization": "<your_api_token>"
}`;

    const missingTokenCode = `const axios = require("axios");

async function testMissingToken() {
  try {
    const response = await axios.get(
      "https://www.onemap.gov.sg/api/common/elastic/search?searchVal=200640&returnGeom=Y&getAddrDetails=Y&pageNum=1"
    );
    console.log(response.data);
  } catch (err) {
    console.error("Status:", err.response?.status);
    console.error("Data:", err.response?.data);
  }
}

testMissingToken();`;

    const missingTokenResponse = `{
  "error": "Authentication token missing. Please create an account and generate or renew your API Token.",
  "found": 0,
  "totalNumPages": 0,
  "pageNum": 0,
  "results": []
}`;

    const expiredTokenCode = `async function testExpiredToken() {
  try {
    const response = await axios.get(
      "https://www.onemap.gov.sg/api/common/elastic/search?searchVal=200640&returnGeom=Y&getAddrDetails=Y&pageNum=1",
      {
        headers: { Authorization: "<expired_api_token>" }
      }
    );
    console.log(response.data);
  } catch (err) {
    console.error("Status:", err.response?.status);
    console.error("Data:", err.response?.data);
  }
}

testExpiredToken();`;

    const expiredTokenResponse = `{
  "error": "Authentication token expired. Tokens is valid for 3 days. Please implement automatic renewal to ensure your token remains valid.",
  "found": 0,
  "totalNumPages": 0,
  "pageNum": 0,
  "results": []
}`;

    const invalidTokenCode = `async function testInvalidToken() {
  try {
    const response = await axios.get(
      "https://www.onemap.gov.sg/api/common/elastic/search?searchVal=200640&returnGeom=Y&getAddrDetails=Y&pageNum=1",
      {
        headers: { Authorization: "invalid12345" }
      }
    );
    console.log(response.data);
  } catch (err) {
    console.error("Status:", err.response?.status);
    console.error("Data:", err.response?.data);
  }
}

testInvalidToken();`;

    const invalidTokenResponse = `{
  "error": "Invalid authentication token. Please register for an account and provide a valid API token.",
  "found": 0,
  "totalNumPages": 0,
  "pageNum": 0,
  "results": []
}`;

    const validTokenCode = `async function testValidToken() {
  try {
    const response = await axios.get(
      "https://www.onemap.gov.sg/api/common/elastic/search?searchVal=200640&returnGeom=Y&getAddrDetails=Y&pageNum=1",
      {
        headers: { Authorization: "<valid_api_token>" }
      }
    );
    console.log("Status:", response.status);
    console.log("Data:", response.data);
  } catch (err) {
    console.error(err.response?.data || err.message);
  }
}

testValidToken();`;

    const validTokenResponse = `{
  "found": 1,
  "totalNumPages": 1,
  "pageNum": 1,
  "results": [
    {
      "SEARCHVAL": "640 ROWELL ROAD SINGAPORE 200640",
      "BLK_NO": "640",
      "ROAD_NAME": "ROWELL ROAD",
      "BUILDING": "NIL",
      "ADDRESS": "640 ROWELL ROAD SINGAPORE 200640",
      "POSTAL": "200640",
      "X": "30381.1007417506",
      "Y": "32195.1006872542",
      "LATITUDE": "1.30743547948389",
      "LONGITUDE": "103.854713903431"
    }
  ]
}`;

    return (
        <>
            <Helmet>
                <title>Verifying Search API Token Implementation | OneMap API</title>
                <meta
                    name="description"
                    content="Verifying Search API Token Implementation"
                />
            </Helmet>
            <div className="om-body container-fluid">
                <HeaderRegComponent />

                <div className="om-body-content pageBase">
                    <div className='documentation center om-body-container'>
                        <div className='row mb-4'>
                            <div className="col-md-10 offset-md-1">
                                <h1 className="text-2xl font-bold text-gray-900">OneMap API</h1>

                                <h1 className="text-3xl font-bold text-gray-900 mb-6">
                                    Verifying Search API Token Implementation
                                </h1>

                                <p>
                                    Search API requests must include a valid <b>API token</b> in the Authorization header.
                                </p>
                                <p>Example:<br/></p>
                                <div class='codeblock'>
                                    <CopyBlock
                                        text={headerExample}
                                        className="abc"
                                        language="javascript"
                                        theme={github}
                                        wrapLines={true}
                                        codeBlock
                                    />
                                    </div>

                                <p>
                                    This section helps you verify that your application has implemented token authentication correctly.
                                </p>

                                <ul>
                                    <li>If the token is missing, expired, or invalid, the API will respond with:
                                        <ul>
                                            <li>An error attribute in the JSON response</li>
                                        </ul>
                                    </li>
                                    <li>
                                        If the request is successful, the error attribute will not be present and the status code will be 200 OK.
                                    </li>
                                </ul>

                                <h2>Sample Requests and Responses (Node.js + Axios)</h2>

                                <h3>1. Missing Token</h3>
                                <div class='codeblock'>
                                    <CopyBlock
                                        text={missingTokenCode}
                                        className="abc"
                                        language="javascript"
                                        theme={github}
                                        wrapLines={true}
                                        codeBlock
                                    />
                                </div>

                                <h4><b>Response (200 OK):</b></h4>
                                <div class='codeblock'>
                                    <CopyBlock
                                        text={missingTokenResponse}
                                        className="abc"
                                        language="javascript"
                                        theme={github}
                                        wrapLines={true}
                                        codeBlock
                                    />
                                </div>
                                

                                <h3 style={{"margin": "40px 0 20px 0"}}>2. Expired Token</h3>
                                <div class='codeblock'>
                                    <CopyBlock
                                        text={expiredTokenCode}
                                        className="abc"
                                        language="javascript"
                                        theme={github}
                                        wrapLines={true}
                                        codeBlock
                                    />
                                </div>

                                <h4><b>Response (200 OK):</b></h4>
                                <div class='codeblock'>
                                    <CopyBlock
                                        text={expiredTokenResponse}
                                        className="abc"
                                        language="javascript"
                                        theme={github}
                                        wrapLines={true}
                                        codeBlock
                                    />
                                </div>

                                <h3 style={{"margin": "40px 0 20px 0"}}>3. Invalid Token</h3>
                                <div class='codeblock'>
                                    <CopyBlock
                                        text={invalidTokenCode}
                                        className="abc"
                                        language="javascript"
                                        theme={github}
                                        wrapLines={true}
                                        codeBlock
                                    />
                                </div>

                                <h4><b>Response (200 OK):</b></h4>
                                <div class='codeblock'>
                                    <CopyBlock
                                        text={invalidTokenResponse}
                                        className="abc"
                                        language="javascript"
                                        theme={github}
                                        wrapLines={true}
                                        codeBlock
                                    />
                                </div>

                                <h3 style={{"margin": "40px 0 20px 0"}}>4. Successful Request (Valid Token)</h3>
                                <div class='codeblock'>
                                    <CopyBlock
                                        text={validTokenCode}
                                        className="abc"
                                        language="javascript"
                                        theme={github}
                                        wrapLines={true}
                                        codeBlock
                                    />
                                </div>

                                <h4><b>Response (200 OK, no error field):</b></h4>
                                <div class='codeblock'>
                                    <CopyBlock
                                        text={validTokenResponse}
                                        className="abc"
                                        language="javascript"
                                        theme={github}
                                        wrapLines={true}
                                        codeBlock
                                    />
                                </div>
                                

                                <h2 style={{"margin": "40px 0 20px 0"}}>Summary Table</h2>
                                <div className="overflow-x-auto">
                                    <table className="min-w-full bg-white border border-black-200"  style={{"border": "2 px solid black"}}>
                                        <thead className="bg-gray-50">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                    Condition
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                    Axios Request Header Example
                                                </th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                                                    Response Behavior
                                                </th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            <tr>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <b>Missing Token</b>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900 font-mono">
                                                    (No <code>Authorization</code> header)
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900">
                                                    Returns "error": "Authentication token missing..." with results.
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <b>Expired Token</b>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900 font-mono">
                                                    <code>Authorization: &lt;expired_api_token&gt;</code>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900">
                                                    Returns "error": "Authentication token expired..." with results.
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <b>Invalid Token</b>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900 font-mono">
                                                    <code>Authorization: invalid12345</code>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900">
                                                    Returns "error": "Invalid authentication token..." with results.
                                                </td>
                                            </tr>
                                            <tr>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <b>Valid Token</b>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900 font-mono">
                                                    <code>Authorization: &lt;valid_api_token&gt;</code>
                                                </td>
                                                <td className="px-6 py-4 text-sm text-gray-900">
                                                    Response does not contain error. Results are returned.
                                                </td>
                                            </tr>
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                <FooterComponent />
            </div>
            </div>
        </>
    );
};

export default VerifyingSearchAPIToken;